// Routes.
//
// Every URL in the ticket app is keyed on `public_ref`, the customer-visible ticket
// number — not `tickets.id` — so references carried over from Zendesk still resolve
// after the migration (docs/API.md §3.2).

import { BrowserRouter, Routes, Route, Navigate } from 'react-router';

import { SessionProvider } from './session/SessionProvider.jsx';
import AppLayout from './layout/AppLayout.jsx';
import { assertRoutesCoverSections } from './layout/nav-items.js';
import TicketList from './pages/tickets/List.jsx';
import TicketDetail from './pages/tickets/Detail.jsx';
import TicketNew from './pages/tickets/New.jsx';
import AdminConsole from './pages/admin/Console.jsx';
import AdminSchedules from './pages/admin/Schedules.jsx';
import AdminSettings from './pages/admin/Settings.jsx';
import AdminIntegrations from './pages/admin/Integrations.jsx';
import IntegrationEdit from './pages/admin/IntegrationEdit.jsx';
import AdminResourceList from './pages/admin/ResourceList.jsx';
import AdminResourceEdit from './pages/admin/ResourceEdit.jsx';
import CustomerList from './pages/customers/List.jsx';
import CustomerDetail from './pages/customers/Detail.jsx';
import SendLists from './pages/customers/SendLists.jsx';
import Reports from './pages/reports/Reports.jsx';
import NotFound from './pages/NotFound.jsx';

// Every path the tree below renders. Declared once, here, so the nav can be held
// to it: `assertRoutesCoverSections` throws at import time — at boot, in the
// browser console — if a section in nav-items.js has no route. That is the sync
// the two hardcoded lists never had.
const ROUTE_PATHS = [
  '/', '/tickets', '/tickets/new', '/tickets/:publicRef',
  '/customers', '/customers/send-lists', '/customers/:id',
  '/reports', '/reports/:reportId',
  '/admin', '/admin/schedules', '/admin/settings',
  '/admin/integrations', '/admin/integrations/:id',
  '/admin/:resource', '/admin/:resource/:id',
  '*',
];
assertRoutesCoverSections(ROUTE_PATHS);

export default function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<Navigate to="/tickets" replace />} />

            <Route path="/tickets" element={<TicketList />} />
            {/* `new` before `:publicRef` so the create form is not read as a ticket
                number. public_ref is numeric, but the router does not know that. */}
            <Route path="/tickets/new" element={<TicketNew />} />
            <Route path="/tickets/:publicRef" element={<TicketDetail />} />

            <Route path="/customers" element={<CustomerList />} />
            {/* Static before dynamic, as with /tickets/new: react-router ranks a
                literal segment above a param, but the order is stated anyway so the
                reason is on the page rather than in the router docs. */}
            <Route path="/customers/send-lists" element={<SendLists />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />

            {/* Both paths render the same component; it reads :reportId to pick
                which report is open, so /reports lands on the default. */}
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/:reportId" element={<Reports />} />

            {/* Deep links need no server change: src/server.js:435 mounts a
                catch-all GET after express.static and serves index.html for any
                unmatched path. The API is what refuses a non-lead, not this. */}
            <Route path="/admin" element={<AdminConsole />} />
            {/* Static, so react-router ranks it above `/admin/:resource`
                regardless of declaration order. The schedule grid is one of the
                three screens design §7 says cannot be generic, so it does not go
                through the descriptor framework — and there is deliberately no
                `agent_schedules` entry in DESCRIPTORS for `/admin/:resource` to
                find. */}
            <Route path="/admin/schedules" element={<AdminSchedules />} />
            {/* Ahead of `/admin/:resource` for the same reason schedules is: a literal
                segment outranks a param in react-router, and stating the order keeps the
                reason on the page. There is deliberately no `app_settings` descriptor —
                the generic routes would let a caller insert any key it liked, which is
                exactly what the settings registry exists to prevent. */}
            <Route path="/admin/settings" element={<AdminSettings />} />
            {/* Ahead of `/admin/:resource` for the same reason schedules and settings
                are: a literal segment outranks a param in react-router, and stating the
                order keeps the reason on the page rather than in the router docs. There
                is deliberately no `integrations` descriptor — this screen writes a sealed
                credential blob and calls `/test` and `/api-clients`, none of which the
                generic row-id-keyed routes can express.

                `/admin/integrations/new?kind=…` is matched by the `:id` route and
                IntegrationEdit branches on `id === 'new'`, exactly as ResourceEdit does.
                The kind rides in the query string because it is not a resource path: it
                is which FORM to draw, and the created row's id is what the URL becomes. */}
            <Route path="/admin/integrations" element={<AdminIntegrations />} />
            <Route path="/admin/integrations/:id" element={<IntegrationEdit />} />
            {/* `/admin/:resource/new` is matched by the `:id` route and
                ResourceEdit branches on `id === 'new'`. The opposite of what
                /tickets/new needs: there, `new` had to be declared FIRST so it
                was not read as a public_ref. Here the segment is a descriptor
                id, ids are the only other thing that appears in this position,
                and no descriptor is named `new`. */}
            <Route path="/admin/:resource" element={<AdminResourceList />} />
            <Route path="/admin/:resource/:id" element={<AdminResourceEdit />} />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
